/**
 * KanbanColumn Component
 *
 * Represents a status column in the kanban board
 * Supports collapsed and expanded states for space optimization
 */

import React, { useState } from 'react';
import { BoardTaskWithAssignments } from '@/types/board-config.types';
import KanbanCard from './KanbanCard';
import CollapsedColumn from './CollapsedColumn';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { layout } from '@/styles/design-tokens';
import { Icon, IconName } from '@/icons';
import { isCardFrozen } from '@/utils/week';

interface KanbanColumnProps {
  id: string;
  name: string;
  color?: string;
  cards: BoardTaskWithAssignments[];
  /** IDs of cards that just entered the board and should animate in */
  newCardIds?: string[];
  /** Whether this column is collapsed */
  isCollapsed?: boolean;
  /** Callback to toggle collapse state */
  onToggleCollapse?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, statusId: string) => void;
  onCardDragStart?: (
    e: React.DragEvent,
    card: BoardTaskWithAssignments
  ) => void;
  onCardDragEnd?: (e: React.DragEvent) => void;
  onCardClick?: (card: BoardTaskWithAssignments) => void;
  /** Current user role for computing per-card frozen state */
  userRole?: string;
  /** Per-card assignee/mentor/admin scoping check, mirroring the backend's
   * isCardAssignee/isCardMentor authorization. When it returns false, the
   * card cannot be dragged (matches the same gate used by TaskEditPopup). */
  canEditCard?: (card: BoardTaskWithAssignments) => boolean;
}

export default function KanbanColumn({
  id,
  name,
  color,
  cards,
  newCardIds,
  isCollapsed = false,
  onToggleCollapse,
  onDragOver,
  onDrop,
  onCardDragStart,
  onCardDragEnd,
  onCardClick,
  userRole,
  canEditCard,
}: KanbanColumnProps) {
  const colors = useThemeColors();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Allow drop
    setIsDragOver(true);
    onDragOver?.(e);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop?.(e, id);
  };

  // If collapsed, show collapsed view
  if (isCollapsed) {
    return (
      <CollapsedColumn
        id={id}
        name={name}
        color={color}
        cardCount={cards.length}
        onClick={onToggleCollapse || (() => {})}
      />
    );
  }

  // Full column view
  return (
    <div
      className="flex-shrink-0 h-full transition-collapse"
      style={{ width: layout.kanban.columnWidth }}
    >
      <div
        className={`rounded-xl p-4 h-full flex flex-col transition-all duration-200 ${
          isDragOver ? 'animate-drop-target scale-[1.02]' : 'shadow-soft'
        }`}
        style={
          {
            backgroundColor: colors.offWhite,
            '--drop-color': color || colors.blue,
            '--drop-color-tint': withOpacity(
              color || colors.blue,
              opacity.subtle
            ),
            '--drop-color-glow': withOpacity(
              color || colors.blue,
              opacity.medium
            ),
          } as React.CSSProperties
        }
      >
        {/* Column Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {color && (
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
            )}
            <h3
              className="font-semibold truncate"
              style={{ color: colors.text.primary }}
              title={name}
            >
              {name}
            </h3>
            <span
              className="text-sm flex-shrink-0"
              style={{ color: colors.text.secondary }}
            >
              ({cards.length})
            </span>
          </div>

          {/* Minimize button - available for all columns */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-1 rounded transition-colors opacity-60 hover:opacity-100 flex-shrink-0"
              style={{ color: colors.text.secondary }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = colors.hover.background;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              aria-label="Collapse column"
              title="Collapse column"
            >
              <Icon name={IconName.MINUS} size="sm" />
            </button>
          )}
        </div>

        {/* Cards Container */}
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden min-h-0"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="space-y-3 pt-1 px-1">
            {cards.map(card => {
              const newIndex = newCardIds ? newCardIds.indexOf(card.id!) : -1;
              return (
                <KanbanCard
                  key={card.id}
                  card={card}
                  isNew={newIndex >= 0}
                  animationDelay={newIndex >= 0 ? newIndex * 80 : 0}
                  onDragStart={onCardDragStart}
                  onDragEnd={onCardDragEnd}
                  onClick={onCardClick}
                  isFrozen={isCardFrozen(card.start_date, userRole)}
                  isEditForbidden={canEditCard ? !canEditCard(card) : false}
                />
              );
            })}
          </div>
          {cards.length === 0 && (
            <div
              className="text-center py-8"
              style={{ color: colors.text.tertiary }}
            >
              No cards
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
